import { STEPS } from "@aybuben/client/onboarding/steps.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { errorKey } from "../../auth/copy.ts";
import { capture } from "../../capture.ts";
import { useStrings, type UiKey } from "../../i18n/index.tsx";
import { COL, FOCUS, UI } from "../../theme.ts";
import { LangPill } from "../LangPill.tsx";
import { reducedMotion } from "../motion.ts";
import { Btn } from "../primitives.tsx";
import { StepArt } from "./art.tsx";

export interface OnboardingProps {
	/** Welcome: before any account, ends by starting a guest. Tour: a signed-in account, ends with Continue. */
	mode: "welcome" | "tour";
	lang: LangId;
	onLang: (l: LangId) => void;
	/** The last screen's button. May reject (a guest cannot start offline). */
	onFinish: () => Promise<void> | void;
	onHaveAccount?: () => void;
}

const LAST = STEPS.length - 1;

export function Onboarding({ mode, lang, onLang, onFinish, onHaveAccount }: OnboardingProps) {
	const t = useStrings();
	const [index, setIndex] = useState(0);
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<UiKey | null>(null);
	const track = useRef<HTMLDivElement>(null);
	const headings = useRef<(HTMLHeadingElement | null)[]>([]);
	// A programmatic smooth scroll passes the slides in between; scroll events
	// are ignored until it lands here.
	const target = useRef<number | null>(null);
	const moved = useRef(false);
	// index is only ever set through go() or onScroll, both clamped to 0..LAST.
	const step = STEPS[index]!;

	useEffect(() => {
		capture("onboarding_step_viewed", { step: step.id, mode });
		const el = track.current;
		if (el?.clientWidth)
			el.scrollTo({ left: index * el.clientWidth, behavior: reducedMotion() ? "auto" : "smooth" });
		// Not on arrival: focus stays with the page until the learner moves.
		if (moved.current) headings.current[index]?.focus({ preventScroll: true });
	}, [index]);

	const go = (i: number) => {
		const next = Math.max(0, Math.min(LAST, i));
		if (next === index) return;
		moved.current = true;
		target.current = next;
		setIndex(next);
	};

	const onScroll = () => {
		const el = track.current;
		if (!el?.clientWidth) return;
		const i = Math.max(0, Math.min(LAST, Math.round(el.scrollLeft / el.clientWidth)));
		if (target.current !== null) {
			if (i === target.current) target.current = null;
			return;
		}
		if (i !== index) {
			moved.current = true;
			setIndex(i);
		}
	};

	const onKey = (e: KeyboardEvent) => {
		// The language select uses arrows for itself.
		if (e.target instanceof HTMLSelectElement) return;
		if (e.key === "ArrowRight") go(index + 1);
		else if (e.key === "ArrowLeft") go(index - 1);
		else return;
		e.preventDefault();
	};

	const skip = () => {
		capture("onboarding_skipped", { step: step.id });
		go(LAST);
	};

	const finish = async () => {
		if (busy) return;
		setBusy(true);
		setErr(null);
		try {
			await onFinish();
			capture("onboarding_completed", { mode });
		} catch (e) {
			setErr(errorKey(e));
		} finally {
			setBusy(false);
		}
	};

	const primary =
		index < LAST
			? t(mode === "welcome" && index === 0 ? "onboarding.getStarted" : "onboarding.next")
			: t(mode === "welcome" ? "onboarding.tryLesson" : "onboarding.continue");

	return (
		<div
			className="fixed inset-0 z-50 flex justify-center"
			style={{ background: COL.bg, color: COL.ink, fontFamily: UI }}
		>
			<div
				className="flex h-full w-full max-w-md flex-col"
				style={{
					paddingTop: "env(safe-area-inset-top)",
					paddingBottom: "max(16px, env(safe-area-inset-bottom))",
				}}
			>
				<div className="flex h-14 items-center justify-between px-4">
					{index === 0 ? <LangPill lang={lang} onLang={onLang} /> : <span />}
					{index > 0 && index < LAST && (
						<button
							type="button"
							onClick={skip}
							className={`rounded-lg px-2 py-1 text-sm font-semibold ${FOCUS}`}
							style={{ color: COL.mute }}
						>
							{t("onboarding.skip")}
						</button>
					)}
				</div>
				<div
					ref={track}
					onScroll={onScroll}
					onKeyDown={onKey}
					role="region"
					aria-roledescription="carousel"
					aria-label={t("onboarding.label")}
					className="flex flex-1 snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overflow-y-hidden"
				>
					{STEPS.map((s, i) => (
						<section
							key={s.id}
							aria-roledescription="slide"
							aria-label={t("onboarding.slide", { n: i + 1, total: STEPS.length })}
							aria-hidden={i !== index}
							inert={i !== index}
							className="flex w-full shrink-0 snap-center flex-col justify-center px-6"
						>
							<StepArt id={s.id} active={i === index} />
							<h2
								ref={(el) => {
									headings.current[i] = el;
								}}
								tabIndex={-1}
								className="mt-6 text-2xl leading-tight font-extrabold focus:outline-none"
							>
								{t(s.title)}
							</h2>
							<p className="mt-3 leading-relaxed" style={{ color: COL.mute }}>
								{t(s.body)}
							</p>
						</section>
					))}
				</div>
				<div className="my-3 flex justify-center gap-1.5" aria-hidden>
					{STEPS.map((s, i) => (
						<span
							key={s.id}
							className="h-2 rounded-full transition-all"
							style={{ width: i === index ? 20 : 8, background: i === index ? COL.pom : COL.line }}
						/>
					))}
				</div>
				<div className="px-4">
					<Btn
						onClick={() => (index < LAST ? go(index + 1) : void finish())}
						disabled={busy}
						color={COL.pom}
					>
						{busy ? t("auth.busy") : primary}
					</Btn>
					{err && (
						<p
							role="alert"
							className="mt-3 text-center text-sm font-semibold"
							style={{ color: COL.red }}
						>
							{t(err)}
						</p>
					)}
					{mode === "welcome" && index === 0 && onHaveAccount && (
						<button
							type="button"
							onClick={onHaveAccount}
							className={`mt-3 w-full rounded-lg py-2 text-sm font-semibold ${FOCUS}`}
							style={{ color: COL.lapis }}
						>
							{t("onboarding.haveAccount")}
						</button>
					)}
					{mode === "welcome" && index === LAST && (
						<p className="mt-3 text-center text-sm" style={{ color: COL.mute }}>
							{t("onboarding.noAccount")}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
